export interface StoredTaskSelection {
    envID: string;
    branches: Record<string, string>;
    model?: string;
    reasoningLevel?: string;
}

export type CreateTaskAttachmentStatus =
    | 'pending'
    | 'too_many_attachments'
    | 'too_large'
    | 'creating'
    | 'uploading'
    | 'scanning'
    | 'ready'
    | 'error';

export interface StoredCreateTaskAttachment {
    clientID: string;
    fileID?: string;
    name: string;
    size: number;
    contentType?: string;
    status: CreateTaskAttachmentStatus;
    progress: number;
    error?: string;
}

const STORAGE_KEY = 'event-horizon:create-task-selection';
const CREATE_TASK_PROMPT_KEY = 'event-horizon:create-task-prompt';
const CREATE_TASK_ATTACHMENTS_KEY = 'event-horizon:create-task-attachments';
const TASK_TURN_PROMPT_PREFIX = 'event-horizon:task-turn-draft:';
const TASK_REASONING_LEVEL_PREFIX = 'event-horizon:task-reasoning-level:';

const CREATE_TASK_ATTACHMENT_STATUSES = new Set<CreateTaskAttachmentStatus>([
    'pending',
    'too_many_attachments',
    'too_large',
    'creating',
    'uploading',
    'scanning',
    'ready',
    'error',
]);

const PERSISTABLE_CREATE_TASK_ATTACHMENT_STATUSES = new Set<CreateTaskAttachmentStatus>([
    'scanning',
    'ready'
]);

function getStorage(): Storage | null {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        return window.localStorage;
    } catch (err) {
        console.warn('Accessing localStorage failed', err);
        return null;
    }
}

function parseStoredCreateTaskAttachment(value: unknown): StoredCreateTaskAttachment | null {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const candidate = value as Partial<StoredCreateTaskAttachment>;
    if (
        typeof candidate.clientID !== 'string'
        || typeof candidate.name !== 'string'
        || typeof candidate.size !== 'number'
        || Number.isNaN(candidate.size)
        || typeof candidate.progress !== 'number'
        || Number.isNaN(candidate.progress)
        || typeof candidate.status !== 'string'
        || !CREATE_TASK_ATTACHMENT_STATUSES.has(candidate.status as CreateTaskAttachmentStatus)
        || !PERSISTABLE_CREATE_TASK_ATTACHMENT_STATUSES.has(candidate.status as CreateTaskAttachmentStatus)
    ) {
        return null;
    }

    const attachment: StoredCreateTaskAttachment = {
        clientID: candidate.clientID,
        name: candidate.name,
        size: candidate.size,
        progress: candidate.progress,
        status: candidate.status as CreateTaskAttachmentStatus,
    };

    if (typeof candidate.fileID === 'string') {
        attachment.fileID = candidate.fileID;
    }
    if (typeof candidate.contentType === 'string') {
        attachment.contentType = candidate.contentType;
    }
    if (typeof candidate.error === 'string') {
        attachment.error = candidate.error;
    }

    return attachment;
}

export function loadStoredTaskSelection(): StoredTaskSelection | null {
    const storage = getStorage();
    if (!storage) {
        return null;
    }
    try {
        const raw = storage.getItem(STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as Partial<StoredTaskSelection> | null;
        if (
            !parsed
            || typeof parsed !== 'object'
            || typeof parsed.envID !== 'string'
            || typeof parsed.branches !== 'object'
            || parsed.branches === null
        ) {
            return null;
        }
        const branches: Record<string, string> = {};
        for (const [key, value] of Object.entries(parsed.branches)) {
            if (typeof value === 'string') {
                branches[key] = value;
            }
        }
        const model = typeof parsed.model === 'string' ? parsed.model : undefined;
        const reasoningLevel = typeof parsed.reasoningLevel === 'string' ? parsed.reasoningLevel : undefined;
        return { envID: parsed.envID, branches, model, reasoningLevel };
    } catch (err) {
        console.warn('Failed to load stored task selection', err);
        return null;
    }
}

export function saveStoredTaskSelection(selection: StoredTaskSelection): void {
    const storage = getStorage();
    if (!storage) {
        return;
    }
    try {
        storage.setItem(STORAGE_KEY, JSON.stringify(selection));
    } catch (err) {
        console.warn('Failed to save task selection', err);
    }
}

export function loadStoredCreateTaskAttachments(): StoredCreateTaskAttachment[] {
    const storage = getStorage();
    if (!storage) {
        return [];
    }

    try {
        const raw = storage.getItem(CREATE_TASK_ATTACHMENTS_KEY);
        if (!raw) {
            return [];
        }

        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed
            .map((attachment) => parseStoredCreateTaskAttachment(attachment))
            .filter((attachment): attachment is StoredCreateTaskAttachment => attachment !== null);
    } catch (err) {
        console.warn('Failed to load stored create task attachments', err);
        return [];
    }
}

export function saveStoredCreateTaskAttachments(attachments: StoredCreateTaskAttachment[]): void {
    const storage = getStorage();
    if (!storage) {
        return;
    }

    try {
        const persistableAttachments = attachments.filter((attachment) => PERSISTABLE_CREATE_TASK_ATTACHMENT_STATUSES.has(attachment.status));

        if (persistableAttachments.length === 0) {
            storage.removeItem(CREATE_TASK_ATTACHMENTS_KEY);
            return;
        }

        storage.setItem(CREATE_TASK_ATTACHMENTS_KEY, JSON.stringify(persistableAttachments));
    } catch (err) {
        console.warn('Failed to save create task attachments', err);
    }
}

export function clearStoredCreateTaskAttachments(): void {
    const storage = getStorage();
    if (!storage) {
        return;
    }

    try {
        storage.removeItem(CREATE_TASK_ATTACHMENTS_KEY);
    } catch (err) {
        console.warn('Failed to clear create task attachments', err);
    }
}

export function loadStoredCreateTaskPrompt(): string {
    const storage = getStorage();
    if (!storage) {
        return '';
    }
    try {
        const value = storage.getItem(CREATE_TASK_PROMPT_KEY);
        return typeof value === 'string' ? value : '';
    } catch (err) {
        console.warn('Failed to load stored create task prompt', err);
        return '';
    }
}

export function saveStoredCreateTaskPrompt(prompt: string): void {
    const storage = getStorage();
    if (!storage) {
        return;
    }
    try {
        if (prompt) {
            storage.setItem(CREATE_TASK_PROMPT_KEY, prompt);
        } else {
            storage.removeItem(CREATE_TASK_PROMPT_KEY);
        }
    } catch (err) {
        console.warn('Failed to save create task prompt', err);
    }
}

export function clearStoredCreateTaskPrompt(): void {
    const storage = getStorage();
    if (!storage) {
        return;
    }
    try {
        storage.removeItem(CREATE_TASK_PROMPT_KEY);
    } catch (err) {
        console.warn('Failed to clear create task prompt', err);
    }
}

export function loadStoredTaskTurnDraft(taskID: string): string {
    if (!taskID) {
        return '';
    }
    const storage = getStorage();
    if (!storage) {
        return '';
    }
    try {
        const value = storage.getItem(`${TASK_TURN_PROMPT_PREFIX}${taskID}`);
        return typeof value === 'string' ? value : '';
    } catch (err) {
        console.warn('Failed to load stored task turn draft', err);
        return '';
    }
}

export function saveStoredTaskTurnDraft(taskID: string, draft: string): void {
    if (!taskID) {
        return;
    }
    const storage = getStorage();
    if (!storage) {
        return;
    }
    try {
        if (draft) {
            storage.setItem(`${TASK_TURN_PROMPT_PREFIX}${taskID}`, draft);
        } else {
            storage.removeItem(`${TASK_TURN_PROMPT_PREFIX}${taskID}`);
        }
    } catch (err) {
        console.warn('Failed to save task turn draft', err);
    }
}

export function clearStoredTaskTurnDraft(taskID: string): void {
    if (!taskID) {
        return;
    }
    const storage = getStorage();
    if (!storage) {
        return;
    }
    try {
        storage.removeItem(`${TASK_TURN_PROMPT_PREFIX}${taskID}`);
    } catch (err) {
        console.warn('Failed to clear task turn draft', err);
    }
}

export function loadStoredTaskReasoningLevel(taskID: string): string {
    if (!taskID) {
        return '';
    }
    const storage = getStorage();
    if (!storage) {
        return '';
    }
    try {
        const value = storage.getItem(`${TASK_REASONING_LEVEL_PREFIX}${taskID}`);
        return typeof value === 'string' ? value : '';
    } catch (err) {
        console.warn('Failed to load stored task reasoning level', err);
        return '';
    }
}

export function saveStoredTaskReasoningLevel(taskID: string, level: string): void {
    if (!taskID) {
        return;
    }
    const storage = getStorage();
    if (!storage) {
        return;
    }
    try {
        if (level) {
            storage.setItem(`${TASK_REASONING_LEVEL_PREFIX}${taskID}`, level);
        } else {
            storage.removeItem(`${TASK_REASONING_LEVEL_PREFIX}${taskID}`);
        }
    } catch (err) {
        console.warn('Failed to save task reasoning level', err);
    }
}
